import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkle, ArrowsClockwise, ChatCircleDots, ListChecks, Folder,
  Briefcase, CalendarBlank, UsersThree, ArrowRight, Sun, Moon, ChartBar,
  Storefront, BookmarkSimple,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { NewProjectModal } from "./ProjectsList";
import HSTClock from "../components/HSTClock";

/**
 * HomeDashboard — the Command Center landing view.
 *
 * Structure:
 *   1. Greeting + AI Daily Brief hero
 *   2. Today's overview strip (5 mini cards)
 *   3. 6-card grid (Tasks / Messages / Projects / Jobs / Events / Meetups)
 *   4. Quick actions floating rail (bottom-right on desktop)
 *
 * Phase 1 wires the layout and the AI brief. Card counts come from a
 * lightweight `/dashboard/summary` endpoint that returns zeros where
 * the underlying feature isn't built yet — no empty-state gymnastics.
 */
export default function HomeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [brief, setBrief] = useState({ text: "", loading: true, error: false });
  const [summary, setSummary] = useState(null);
  const [newProjectOpen, setNewProjectOpen] = useState(false);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, []);
  const firstName = (user?.name || user?.username || "there").split(" ")[0];

  const fetchBrief = async (force = false) => {
    setBrief((b) => ({ ...b, loading: true, error: false }));
    try {
      const { data } = force
        ? await api.post("/ai/daily-brief/refresh")
        : await api.get("/ai/daily-brief");
      setBrief({ text: data.brief || "", loading: false, error: false });
    } catch (ex) {
      setBrief({
        text: `${greeting}, ${firstName}. Log in to unlock your personalized brief.`,
        loading: false,
        error: true,
      });
    }
  };

  const fetchSummary = async () => {
    try {
      const { data } = await api.get("/dashboard/summary");
      setSummary(data);
    } catch { setSummary(null); }
  };

  useEffect(() => {
    if (user) {
      fetchBrief();
      fetchSummary();
    } else {
      setBrief({ text: "", loading: false, error: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const cards = useMemo(() => ([
    {
      key: "tasks", label: "Tasks", to: "/app/tasks", icon: ListChecks,
      primary: summary?.tasks?.open ?? 0,
      caption: `${summary?.tasks?.today ?? 0} due today`,
    },
    {
      key: "messages", label: "Messages", to: "/app/messages", icon: ChatCircleDots,
      primary: summary?.messages?.unread ?? 0, caption: "unread",
    },
    {
      key: "projects", label: "Projects", to: "/app/projects", icon: Folder,
      primary: summary?.projects?.active ?? 0, caption: "active",
    },
    {
      key: "jobs", label: "Jobs", to: "/app/jobs", icon: Briefcase,
      primary: summary?.jobs?.new_this_week ?? 0, caption: "new this week",
    },
    {
      key: "events", label: "Events", to: "/app/events", icon: CalendarBlank,
      primary: summary?.events?.upcoming ?? 0, caption: "upcoming",
    },
    {
      key: "meetups", label: "Meetups", to: "/app/meetups", icon: UsersThree,
      primary: summary?.meetups?.upcoming ?? 0, caption: "upcoming",
    },
  ]), [summary]);

  const isNight = new Date().getHours() >= 18;

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="home-dashboard">
      {/* ── Greeting ─────────────────────────────────────────────── */}
      <motion.header
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#F2DC8C]/60 flex items-center gap-2">
            {isNight ? <Moon size={12}/> : <Sun size={12}/>}
            {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", timeZone: "Pacific/Honolulu" })}
          </div>
          <h1 className="mt-1 font-display text-[36px] sm:text-[44px] leading-tight"
              style={{
                background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                letterSpacing: "0.02em",
              }}>
            {greeting}, {firstName}.
          </h1>
        </div>
        <div className="pb-1">
          <HSTClock variant="hero" testId="home-hst-clock"/>
        </div>
      </motion.header>

      {/* ── AI Daily Brief ───────────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.4 }}
        className="rounded-2xl p-5 sm:p-6 relative overflow-hidden"
        data-testid="home-ai-brief"
        style={{
          background:
            "radial-gradient(circle at 15% 0%, rgba(255,215,0,0.14) 0%, transparent 55%)," +
            "linear-gradient(180deg, rgba(26,18,6,0.85) 0%, rgba(5,3,0,0.9) 100%)",
          border: "1px solid rgba(255,215,0,0.4)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,215,0,0.04)",
          backdropFilter: "blur(20px)",
        }}>
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
            <Sparkle size={14} weight="fill" color="#0a0703"/>
          </span>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#F2DC8C]/85">
            AI Daily Brief
          </div>
          <button
            onClick={() => fetchBrief(true)}
            disabled={brief.loading}
            data-testid="home-ai-refresh"
            className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center transition-transform active:scale-90 disabled:opacity-40"
            style={{ border: "1px solid rgba(212,175,55,0.5)", background: "rgba(0,0,0,0.35)" }}
            aria-label="Refresh brief">
            <ArrowsClockwise size={13} className={brief.loading ? "animate-spin text-[#FFD700]" : "text-[#FFD700]"}/>
          </button>
        </div>

        {brief.loading ? (
          <div className="space-y-2" data-testid="home-ai-loading">
            {[100, 92, 78].map((w, i) => (
              <div key={i} className="h-3 rounded-full animate-pulse"
                   style={{ width: `${w}%`, background: "linear-gradient(90deg, rgba(255,215,0,0.12), rgba(255,215,0,0.04))" }}/>
            ))}
          </div>
        ) : (
          <p data-testid="home-ai-text"
             className="text-[15px] leading-[1.7] text-[#F2DC8C]">
            {brief.text || `${greeting}, ${firstName}. Your Command Center is ready.`}
          </p>
        )}
      </motion.section>

      {/* ── Today's overview strip ───────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
        data-testid="home-overview">
        <OverviewMini label="Tasks due" value={summary?.tasks?.today ?? 0} testid="overview-tasks-today"/>
        <OverviewMini label="Meetings" value={summary?.meetups?.upcoming ?? 0} testid="overview-meetings"/>
        <OverviewMini label="Jobs matched" value={summary?.jobs?.new_this_week ?? 0} testid="overview-jobs"/>
        <OverviewMini label="Nearby events" value={summary?.events?.upcoming ?? 0} testid="overview-events"/>
        <OverviewMini label="Productivity" value="—" testid="overview-productivity" hint="Coming soon"/>
      </motion.section>

      {/* ── Card grid ────────────────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="home-cards">
        {cards.map((c, i) => (
          <ModuleCard key={c.key} card={c} delay={0.05 * i}/>
        ))}
      </motion.section>

      {/* Quick actions rail retired — replaced by the global Triforce FAB. */}

      <AnimatePresence>
        {newProjectOpen && (
          <NewProjectModal
            onClose={() => setNewProjectOpen(false)}
            onCreate={async (payload) => {
              const { data } = await api.post("/projects", payload);
              setNewProjectOpen(false);
              navigate(`/app/projects/${data.project_id}`);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}


function OverviewMini({ label, value, hint, testid }) {
  return (
    <div
      className="rounded-xl p-4 backdrop-blur transition-all hover:scale-[1.02]"
      data-testid={testid}
      style={{
        background: "linear-gradient(180deg, rgba(26,18,6,0.7) 0%, rgba(5,3,0,0.85) 100%)",
        border: "1px solid rgba(212,175,55,0.35)",
      }}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#F2DC8C]/60">{label}</div>
      <div className="mt-1 font-display text-[24px] leading-none text-[#FFD700] tabular-nums">
        {value}
      </div>
      {hint && (
        <div className="text-[9px] uppercase tracking-widest text-[#F2DC8C]/40 mt-1">{hint}</div>
      )}
    </div>
  );
}


function ModuleCard({ card, delay }) {
  const Icon = card.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35 }}>
      <Link
        to={card.to}
        data-testid={`card-${card.key}`}
        className="group block rounded-2xl p-5 transition-all hover:-translate-y-0.5"
        style={{
          background:
            "radial-gradient(circle at 100% 0%, rgba(255,215,0,0.08) 0%, transparent 55%)," +
            "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
          border: "1px solid rgba(212,175,55,0.45)",
          boxShadow: "0 6px 18px rgba(0,0,0,0.45)",
          backdropFilter: "blur(14px)",
        }}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#F2DC8C]/70">
              {card.label}
            </div>
            <div className="mt-2 font-display text-[34px] leading-none text-[#FFD700] tabular-nums"
                 data-testid={`card-${card.key}-value`}>
              {card.primary}
            </div>
            <div className="mt-1 text-[11px] text-[#F2DC8C]/60">
              {card.caption}
            </div>
          </div>
          <span className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)" }}>
            <Icon size={18} weight="fill" color="#0a0703"/>
          </span>
        </div>
        <div className="mt-4 flex items-center justify-between text-[10px] uppercase tracking-[0.22em] text-[#F2DC8C]/50 group-hover:text-[#FFD700] transition-colors">
          Open module
          <ArrowRight size={12}/>
        </div>
      </Link>
    </motion.div>
  );
}

